/**
 * A decision another strategy outranked on its bracket (CU-869ffwj71;
 * since CU-869fhuhy6 the rivals are strategies and products, never the
 * sites of one product — inside one strategy there is no arbiter).
 *
 * Several products may write the same tariff. When two strategies decide
 * the same bracket in one flush, the strategy with the higher priority
 * (the lower number) sets the price; on a tie, the decision that moves
 * the price more; on an equal move, the lower net. The loser is the same
 * `applied` / `capped` / `tethered` row it always was, with
 * `write_status = 'outranked'` and one `outranked_by` block that webback
 * fills with names, never ids: `{strategy_name, priority, loser_priority,
 * broker, event_id, call_id, rule, tie, amount}`. The winner carries
 * `won_over = [{strategy_name, broker, priority, price, event_id, rule}]`.
 *
 * Every string the screen shows for the loser comes from here, so the
 * four rules read the same on the row, in the tooltip and in the detail.
 * A human decision (approve / override) competes with priority 0 and
 * arrives as `strategy_name = "manual approval" | "manual override"`.
 */

const num = (n) => (n == null || n === '' || !Number.isFinite(Number(n)) ? null : Number(n))
const eur = (n) => (num(n) == null ? '—' : `${num(n).toFixed(2)} €`)
//: "−3.40 €" / "+4.10 €" — the sign is the direction of the move.
const signed = (n) => {
	const v = num(n)
	if (v == null) return '—'
	return `${v < 0 ? '−' : '+'}${Math.abs(v).toFixed(2)} €`
}

/** The `outranked_by` block, or null — only on a row whose send was outranked. */
export function outrankedBy(row) {
	if (row?.write_status !== 'outranked') return null
	const ob = row.outranked_by
	return ob && typeof ob === 'object' ? ob : null
}

//: `priority | bigger_move | lower_net | same_amount`; the tie block
//: repeats it, so an old row missing the top-level one still reads.
const ruleOf = (ob) => ob.rule || ob.tie?.rule || 'priority'
const winnerAmount = (ob) => ob.amount ?? ob.pushed_price ?? ob.price
const strategy = (ob) => ob.strategy_name || 'another strategy'
const broker = (ob) => ob.broker || 'another broker'

/**
 * The sub-label under the Resolution chip, in parts so the strategy can
 * be bold: `{lead: 'by', strategy, tail: '· DiscoverCars'}` or
 * `{lead: 'tie ·', strategy, tail: '· DiscoverCars moved more'}`.
 */
export function outrankedRowParts(row) {
	const ob = outrankedBy(row)
	if (!ob) return null
	const rule = ruleOf(ob)
	if (rule === 'priority') return { lead: 'by', strategy: strategy(ob), tail: `· ${broker(ob)}` }
	const why = rule === 'bigger_move' ? 'moved more'
	          : rule === 'lower_net' ? 'lower net'
	          : 'same amount'
	return { lead: 'tie ·', strategy: strategy(ob), tail: `· ${broker(ob)} ${why}` }
}

/** The same sub-label as one string: `by Lack Volumes · DiscoverCars`. */
export function outrankedRowLabel(row) {
	const p = outrankedRowParts(row)
	return p ? `${p.lead} ${p.strategy} ${p.tail}` : null
}

const tieOpening = (ob) =>
	`Tie: ${strategy(ob)} decided this bracket on ${broker(ob)} too, at the same priority (${ob.priority ?? '—'}).`

/** The chip's tooltip: the rule, in one sentence. */
export function outrankedTitle(row) {
	const ob = outrankedBy(row)
	if (!ob) return null
	const rule = ruleOf(ob)
	let text
	if (rule === 'priority') {
		text = `Decided, never sent: ${strategy(ob)} (priority ${ob.priority ?? '—'}) on ${broker(ob)} decided this bracket too and outranks ${row.strategy_name || 'this strategy'} (priority ${ob.loser_priority ?? '—'}).`
	} else if (rule === 'bigger_move') {
		text = `${tieOpening(ob)} The decision that moves the price more went out.`
	} else if (rule === 'lower_net') {
		text = `${tieOpening(ob)} Equal move; the lower net went out.`
	} else {
		text = `${tieOpening(ob)} Equal move and equal net; one record was kept, the other went out.`
	}
	if (row.simulated) {
		text += ' Simulation: had the ladder been sent, this bracket would have carried the other decision.'
	}
	return text
}

/**
 * The PMS push sentence of the loser's detail. `when` is the hour the
 * ladder went out, already formatted by the caller (the screen owns the
 * locale); without it the sentence simply omits the hour.
 */
export function outrankedSentence(row, { when } = {}) {
	const ob = outrankedBy(row)
	if (!ob) return null
	const rule = ruleOf(ob)
	const amount = eur(winnerAmount(ob))
	const at = when ? ` at ${when}` : ''
	if (row.simulated) {
		const opening = rule === 'priority'
			? `${strategy(ob)} (priority ${ob.priority ?? '—'}) on ${broker(ob)} decided this bracket too and outranks ${row.strategy_name || 'this strategy'} (priority ${ob.loser_priority ?? '—'}).`
			: tieOpening(ob)
		return `Not sent. ${opening} Simulation: nothing went out; had the ladder been sent, this bracket would have carried its ${amount}/day.`
	}
	if (rule === 'priority') {
		return `Not sent. ${strategy(ob)} (priority ${ob.priority ?? '—'}) on ${broker(ob)} decided this bracket too and outranks ${row.strategy_name || 'this strategy'} (priority ${ob.loser_priority ?? '—'}). Its decision, ${amount}/day, went out in the ladder${at}.`
	}
	const t = ob.tie || {}
	if (rule === 'bigger_move') {
		return `Not sent. ${tieOpening(ob)} The decision that moves the price more goes out: ${broker(ob)} moved it ${signed(t.winner_delta)}, this one ${signed(t.loser_delta)}. The ladder went out${at} with ${amount}/day.`
	}
	if (rule === 'lower_net') {
		const move = num(t.winner_delta) == null ? '' : ` (${eur(Math.abs(num(t.winner_delta)))} each)`
		return `Not sent. ${tieOpening(ob)} Equal move${move}; the lower net goes out: ${amount}/day on ${broker(ob)} against ${eur(row.new_price)}/day here. The ladder went out${at} with ${amount}/day.`
	}
	return `Not sent. ${tieOpening(ob)} Equal move and equal net; one record was kept, the other went out. The ladder went out${at} with ${amount}/day.`
}

/**
 * The truth the operator has to know: the tariff has one net, and this
 * row's broker will publish the winner's amount with its own markup.
 */
export function oneNetNote(row) {
	const ob = outrankedBy(row)
	if (!ob) return null
	return `One net per tariff: ${row.scope?.source || 'this broker'} publishes ${eur(winnerAmount(ob))} with its own markup, wherever that lands it.`
}

/** `[{...}]` or `[]` — the decisions this row won over, whatever shape arrived. */
export function wonOverEntries(row) {
	const v = row?.won_over
	return Array.isArray(v) ? v : []
}

/**
 * The winner's line: "Won this bracket over 1 other decision: Regular rule
 * on Sunny Cars (priority 50, 35.80 €) — kept in the Activity as
 * outranked." Null for a winner with no rival — it shows nothing new.
 */
export function wonOverSentence(row) {
	const list = wonOverEntries(row)
	if (!list.length) return null
	const names = list.map(w => {
		const bits = []
		if (w.priority != null) bits.push(`priority ${w.priority}`)
		const price = w.price ?? w.amount
		if (num(price) != null) bits.push(eur(price))
		return `${w.strategy_name || 'another strategy'} on ${w.broker || 'another broker'}${bits.length ? ` (${bits.join(', ')})` : ''}`
	})
	return `Won this bracket over ${list.length} other decision${list.length === 1 ? '' : 's'}: ${names.join(', ')} — kept in the Activity as outranked.`
}
